// ===== Panel Resizer (오른쪽 패널 폭 조절 공용 유틸) =====
// 파일 서랍(features/room-file-drawer)과 파일 미리보기 패널(features/artifact-panel)이 공유한다.
// 두 표면 모두 오른쪽에 붙는 패널이라 "왼쪽으로 끌수록 넓어진다"가 같고, 폭 클램프·localStorage
// 기억·키보드 조작·aria 값 갱신도 같다 — 각 표면에 자체 드래그 계산을 재도입하지 않는다
// (utils/popup-position.ts 선례와 같은 이유).
//
// 폭은 target 요소의 CSS 커스텀 프로퍼티 하나로만 흘려보낸다. 패널과 손잡이가 같은 변수를
// 읽으므로 둘의 위치가 어긋날 여지가 없다.

interface PanelResizerOptions {
  /** 손잡이 요소. role="separator" + tabindex 를 마크업이 갖고 있어야 한다. */
  handle: HTMLElement;
  /** cssVariable 을 쓸 요소. 패널과 손잡이가 그 변수를 상속받는 조상이어야 한다. */
  target: HTMLElement | null;
  cssVariable: string;
  storageKey: string;
  defaultWidth: number;
  minWidth: number;
  maxWidth: number;
  /** 반대편(대화·본문)에 남겨 둘 최소 폭 — 패널이 화면을 통째로 덮는 걸 막는다. */
  minRemaining: number;
  /** 가용 폭 측정. 0 이하를 돌려주면(레이아웃 없는 환경 등) 가용 폭 상한을 건너뛴다. */
  measureAvailable: () => number;
}

interface PanelResizer {
  getWidth(): number;
  setWidth(value: number, persist?: boolean): void;
  setVisible(visible: boolean): void;
}

const PANEL_RESIZER_STEP = 16;

function attachPanelResizer(options: PanelResizerOptions): PanelResizer {
  const { handle, cssVariable, storageKey, defaultWidth, minWidth, maxWidth, minRemaining } = options;
  let width = defaultWidth;

  function clampWidth(value: number): number {
    if (!Number.isFinite(value)) return defaultWidth;
    const available = options.measureAvailable();
    const upper = available > 0
      ? Math.max(minWidth, Math.min(maxWidth, available - minRemaining))
      : maxWidth;
    return Math.round(Math.min(Math.max(value, minWidth), upper));
  }

  function readStoredWidth(): number {
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return defaultWidth;
      const parsed = Number.parseInt(raw, 10);
      return Number.isFinite(parsed) ? parsed : defaultWidth;
    } catch (_error: unknown) {
      // 사생활 모드·저장소 차단에서는 기본 폭으로 조용히 간다.
      return defaultWidth;
    }
  }

  function setWidth(value: number, persist = false): void {
    width = clampWidth(value);
    options.target?.style.setProperty(cssVariable, `${width}px`);
    handle.setAttribute("aria-valuenow", String(width));
    handle.setAttribute("aria-valuemin", String(minWidth));
    handle.setAttribute("aria-valuemax", String(maxWidth));
    if (!persist) return;
    try {
      localStorage.setItem(storageKey, String(width));
    } catch (_error: unknown) {
      // 저장 실패가 이번 세션의 폭 변경까지 되돌릴 이유는 못 된다.
    }
  }

  handle.addEventListener("pointerdown", (event) => {
    if (event.button !== 0) return;
    const startX = event.clientX;
    const startWidth = width;
    handle.dataset.dragging = "true";
    handle.setPointerCapture?.(event.pointerId);

    // 패널이 오른쪽에 붙어 있으므로 왼쪽으로 끌수록 넓어진다.
    const onMove = (moveEvent: PointerEvent) => { setWidth(startWidth + (startX - moveEvent.clientX)); };
    const onEnd = () => {
      handle.dataset.dragging = "false";
      handle.removeEventListener("pointermove", onMove);
      handle.removeEventListener("pointerup", onEnd);
      handle.removeEventListener("pointercancel", onEnd);
      setWidth(width, true);
    };
    handle.addEventListener("pointermove", onMove);
    handle.addEventListener("pointerup", onEnd);
    handle.addEventListener("pointercancel", onEnd);
    event.preventDefault();
  });

  handle.addEventListener("keydown", (event) => {
    let next = width;
    if (event.key === "ArrowLeft") next = width + PANEL_RESIZER_STEP;
    else if (event.key === "ArrowRight") next = width - PANEL_RESIZER_STEP;
    else if (event.key === "Home") next = minWidth;
    else if (event.key === "End") next = maxWidth;
    else return;
    event.preventDefault();
    setWidth(next, true);
  });

  setWidth(readStoredWidth());

  return {
    getWidth: () => width,
    setWidth,
    setVisible(visible: boolean): void { handle.hidden = !visible; },
  };
}
